import { useCallback, useEffect, useRef, useState } from "react";
import { ChevronDown, ChevronUp, Keyboard } from "lucide-react";

// 343: ovládání seznamu dokladů klávesnicí (jako Linear / Narel): ↑ ↓ (i J K) posun výběru,
// mezerník otevře detail dokladu (355: Enter ne — dělal totéž), ? nápověda. Najetí myší řádek
// vybere taky. V otevřeném detailu ↑ ↓ listují na další doklad a mezerník ho zavře (useDetailNav).
// Nefunguje při psaní do pole, s ⌘/Ctrl/Alt a když je otevřené okno.
const TYPING = ["input", "textarea", "select"];
export const isTypingTarget = (t) => TYPING.includes((t?.tagName || "").toLowerCase()) || !!t?.isContentEditable;
const openDialogs = () => document.querySelectorAll('[role="dialog"], [role="alertdialog"]').length;

// listId: řádky nesou data-nav="<listId>:<index>" — podle toho se vybraný řádek posune do zorného pole.
export function useListNav(listId, count, onOpen, { enabled = true } = {}) {
  const [sel, setSel] = useState(-1);
  const [help, setHelp] = useState(false);
  const ref = useRef({});
  ref.current = { sel, onOpen, count };
  const quiet = useRef(false);   // výběr myší — seznam se neposouvá
  const ptr = useRef(null);

  useEffect(() => { setSel((s) => (s >= count ? count - 1 : s)); }, [count]);

  useEffect(() => {
    if (!enabled) return undefined;
    const h = (e) => {
      const t = e.target;
      const tag = (t?.tagName || "").toLowerCase();
      // ze zaškrtávacího pole jde šipkami dál po seznamu (pole přestane mít kurzor)
      if (tag === "input" && t.type === "checkbox" && (e.key === "ArrowDown" || e.key === "ArrowUp")) t.blur();
      else if (isTypingTarget(t)) return;
      if (e.metaKey || e.ctrlKey || e.altKey || e.defaultPrevented || openDialogs()) return;
      const r = ref.current;
      if (e.key === "?") { setHelp((v) => !v); return; }
      if (!r.count) return;
      if (e.key === "ArrowDown" || e.key === "j") { e.preventDefault(); setSel((s) => Math.min(r.count - 1, s + 1)); }
      else if (e.key === "ArrowUp" || e.key === "k") { e.preventDefault(); setSel((s) => Math.max(0, s - 1)); }
      else if (e.key === " " && r.sel >= 0 && tag !== "button" && tag !== "a") { e.preventDefault(); r.onOpen?.(r.sel); }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [enabled]);

  useEffect(() => {
    if (sel < 0) return;
    const el = document.querySelector(`[data-nav="${listId}:${sel}"]`);
    if (!el) return;
    if (!quiet.current) el.scrollIntoView?.({ block: "nearest" });
    quiet.current = false;
    // 344: kurzor prohlížeče na vybraný řádek — jinak by mezerník mířil na naposled kliknuté
    // tlačítko (záložka, filtr) a „zmáčkly“ ho místo otevření dokladu. Ne pod otevřeným oknem a ne z pole.
    if (!openDialogs() && !isTypingTarget(document.activeElement)) el.focus?.({ preventScroll: true });
  }, [sel, listId]);

  // řádek seznamu: tabIndex -1 (kurzor jen z kódu / klikem), fokus z Tabu nebo kliku vybere řádek.
  // 355: najetí myší vybere taky (mezerník pak otevře řádek pod myší) — jen skutečný pohyb myši,
  // ne řádek, který pod stojící myš přijede, když se seznam posune šipkami.
  // 381: stálá funkce (čte výběr z ref) — memo seznamy (Doklady) se kvůli ní nepřekreslují.
  const rowProps = useCallback((i) => ({ "data-nav": `${listId}:${i}`, "aria-selected": ref.current.sel === i || undefined,
    onFocus: (e) => { if (e.target === e.currentTarget) setSel(i); },
    onMouseMove: (e) => {
      const p = ptr.current;
      if (p && p.x === e.clientX && p.y === e.clientY) return;
      ptr.current = { x: e.clientX, y: e.clientY };
      if (ref.current.sel !== i) { quiet.current = true; setSel(i); }
    } }), [listId]);
  return { sel, setSel, help, setHelp, rowProps };
}

// V otevřeném detailu dokladu: nav = { index, count, onGo }. ↑ ↓ (i J K) přepnou na předchozí /
// další doklad, mezerník zavře (Esc zavírá okno samo). Šipky ne v poli, v rozbalené nabídce ani
// na přepínači (mají vlastní ovládání); mezerník ne na tlačítku; nic, když je nad detailem jiné okno.
const OWN_ARROWS = "[role='combobox'], [role='listbox'], [role='option'], [role='menu'], [role='menuitem'], [role='tab'], [role='radio'], [role='slider']";
const ACTIVE = "button, a, summary, label, [role='button'], [role='checkbox'], [role='switch']";
// 370: `dialogs` = kolik oken je detail sám (vysouvací okno 1, panel vedle tabulky 0) — víc = je nad ním jiné okno.
// 376: letters = false — v detailu dokladu J / K nepřepínají (K = Kategorie), stačí šipky.
export function useDetailNav(nav, open, onClose, { dialogs = 1, letters = true } = {}) {
  const ref = useRef({});
  ref.current = { nav, onClose };
  const on = !!(open && nav);
  useEffect(() => {
    if (!on) return undefined;
    const h = (e) => {
      const t = e.target;
      if (isTypingTarget(t) || e.metaKey || e.ctrlKey || e.altKey || e.defaultPrevented || openDialogs() > dialogs) return;
      const { nav: n, onClose: close } = ref.current;
      if (!n) return;
      const within = (sel) => !!(t?.closest && t.closest(sel));
      if ((e.key === "ArrowDown" || (letters && e.key === "j")) && !within(OWN_ARROWS)) {
        if (n.index < n.count - 1) { e.preventDefault(); n.onGo(n.index + 1); }
      } else if ((e.key === "ArrowUp" || (letters && e.key === "k")) && !within(OWN_ARROWS)) {
        if (n.index > 0) { e.preventDefault(); n.onGo(n.index - 1); }
      } else if (e.key === " " && !within(ACTIVE) && !within(OWN_ARROWS)) { e.preventDefault(); close(); }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [on, dialogs, letters]);
}

// „3 / 8" a šipky v hlavičce detailu dokladu (jen na počítači).
export function DetailNav({ nav, testid = "detail-nav", noun = "doklad" }) {
  if (!nav || nav.count < 2) return null;
  const btn = "w-9 h-9 rounded-full border border-border bg-card grid place-items-center hover:bg-secondary disabled:opacity-35 disabled:pointer-events-none";
  return (
    <div className="hidden md:flex items-center gap-1.5 shrink-0" data-testid={testid}>
      <span className="text-xs font-semibold text-muted-foreground tabular-nums px-1" data-testid={`${testid}-position`}>{nav.index + 1} / {nav.count}</span>
      <button type="button" className={btn} disabled={nav.index <= 0} onClick={() => nav.onGo(nav.index - 1)} aria-label={`Předchozí ${noun}`} title={`Předchozí ${noun} (↑)`} data-testid={`${testid}-prev`}><ChevronUp size={17} /></button>
      <button type="button" className={btn} disabled={nav.index >= nav.count - 1} onClick={() => nav.onGo(nav.index + 1)} aria-label={`Další ${noun}`} title={`Další ${noun} (↓)`} data-testid={`${testid}-next`}><ChevronDown size={17} /></button>
    </div>
  );
}

// Klávesa v textu (na dotykových zařízeních se nezobrazuje).
export function Kbd({ children, className = "", ...rest }) {
  return (
    <kbd {...rest} className={`hidden md:inline-grid place-items-center min-w-[20px] h-5 px-1.5 rounded-[5px] border border-border bg-secondary text-[11px] font-semibold font-sans text-muted-foreground ${className}`}>
      {children}
    </kbd>
  );
}

// Nápověda zkratek — ikona a bublina (jen na počítači), otevře ji i klávesa „?".
export function ListHelp({ open, setOpen, className = "" }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const down = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const esc = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", down);
    window.addEventListener("keydown", esc);
    return () => { document.removeEventListener("mousedown", down); window.removeEventListener("keydown", esc); };
  }, [open, setOpen]);
  const rows = [["↑ ↓  ·  J K", "posun v seznamu"], ["↑ ↓", "další doklad v otevřeném detailu"], ["mezerník", "otevřít vybraný doklad"],
    ["mezerník · Esc", "zavřít doklad"], ["Z · K · L", "zakázka, kategorie, štítek v detailu"], ["?", "tato nápověda"]];
  return (
    <div className={`relative hidden md:block ${className}`} ref={ref}>
      <button type="button" onClick={() => setOpen(!open)} aria-label="Klávesové zkratky" title="Klávesové zkratky (?)" data-testid="keyboard-help-button"
        className="w-8 h-8 rounded-full grid place-items-center text-muted-foreground hover:text-foreground hover:bg-secondary">
        <Keyboard size={16} />
      </button>
      {open && (
        <div role="tooltip" data-testid="keyboard-help"
          className="absolute right-0 top-10 z-40 w-[300px] rounded-[16px] border border-border bg-popover text-popover-foreground shadow-xl p-3.5">
          <div className="font-bold text-sm mb-2">Klávesové zkratky</div>
          {rows.map(([k, t]) => (
            <div key={k} className="flex items-center justify-between gap-3 py-1 text-[13px]">
              <kbd className="rounded-[5px] border border-border bg-secondary px-1.5 py-0.5 text-[11px] font-semibold font-sans whitespace-nowrap">{k}</kbd>
              <span className="text-muted-foreground text-right">{t}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
